import { useAuthStore } from '../store/auth';

// Feature flags must match backend's FEATURE_MAP in constants/entitlements.js.
// We don't fetch the map dynamically — the frontend gate is for UX, the
// backend gate (requireFeature middleware) is authoritative.
const FEATURE_MAP = {
  bulkRewrite: ['starter', 'pro', 'agency', 'enterprise'],
  // Agency-suite features — native to Agency only (no cascade).
  agencyClientWorkspaces: ['agency'],
  whiteLabelReports: ['agency'],
  prioritySupport: ['agency'],
  // Pro features — native to Pro only.
  apiAccess: ['pro'],
  customAiPrompts: ['pro'],
  csvExports: ['pro'],
  contentBriefs: ['pro'],
  citationTracking: ['pro'],
  keywordTracker: ['pro'],
  // Add-on / tier-specific.
  dfyContentLibrary: ['booster'],
  autoPilotScheduler: ['autoPilot'],
  resellerLicensing: ['enterprise'],
  customSubdomains: ['enterprise'],
  customEmailSender: ['enterprise'],
};

function ownedEntitlements(user) {
  const owned = new Set();
  if (!user) return owned;
  const ents = user.entitlements || {};
  for (const k of ['starter', 'booster', 'pro', 'agency', 'enterprise']) {
    if (ents[k]) owned.add(k);
  }
  if (ents.autoPilot?.active) {
    const exp = ents.autoPilot.expiresAt ? new Date(ents.autoPilot.expiresAt) : null;
    if (!exp || exp.getTime() > Date.now()) owned.add('autoPilot');
  }
  return owned;
}

export function hasFeature(user, flag) {
  const conferring = FEATURE_MAP[flag];
  if (!conferring) return false;
  const owned = ownedEntitlements(user);
  return conferring.some((e) => owned.has(e));
}

// React hook — re-renders when the auth store's user changes.
export function useFeature(flag) {
  const user = useAuthStore((s) => s.user);
  return hasFeature(user, flag);
}

// Suggest the lowest-tier upgrade for a locked feature.
export function upgradeHintFor(flag) {
  const tiers = FEATURE_MAP[flag] || [];
  if (tiers.includes('pro')) return 'Pro';
  if (tiers.includes('booster')) return 'Booster Pack';
  if (tiers.includes('autoPilot')) return 'AutoPilot';
  if (tiers.includes('agency')) return 'Agency';
  if (tiers.includes('enterprise')) return 'Enterprise';
  return 'a paid plan';
}
